import { isFeatureFlagEnabled } from '@doordash/lib-feature-flags'
import * as React from 'react'
import { connect } from 'react-redux'
import IdentityVerificationAwait from '../../../../components/molecules/identity-verification-fail'
import * as events from '../../../../events'
import * as Exp from '../../../../experimentConstants'
import * as DA from '../../../../typeModules/events'
import { useVgs, useVgsForm, VGSForm } from '../../../../utilities/hooks'
import { IDVStatus } from '../../../../utilities/steps'
import { IdentificationDataType } from '../types'
import DisclosureStep from './disclosure-step'
import IdentificationStep from './identification-step'
import IdentityVerification, {
  TemplateIds,
  TemplateIdTypes,
} from './identity-verification-step'
import { Hider, ssnFieldStyles } from './styles'
import VehicleStep from './vehicle-step'

type Props = {
  uniqueLink?: string
  error?: string
  doSubmit?: (
    uniqueLink: string,
    backgroundInfo: object,
    form?: VGSForm
  ) => Promise<any>
  vehicleTypes?: any
  trackBackgroundCheckSteps: (data: DA.TrackBackgroundCheck) => Promise<any>
  application?: any,
  idvStatus?: {
    value?: string
  }
}

enum BgcStep {
  VEHICLE_STEP = 1,
  IDENTIFICATION_STEP = 2,
  DISCLOSURE_STEP = 3,
  IDENTITY_VERIFICATION = 4, // This is actually step 2. Didn't update it to change other
  //  existing enum
  IDENTITY_VERIFICATION_FAIL = 5,
}

const defaultVehicleState = {
  vehicleType: -1,
  color: '',
  make: '',
  model: '',
  insuranceCheck: false,
}

const defaultIdentificationState: IdentificationDataType = {
  licenseNumber: '',
  licenseState: '',
  ssn: '',
  birthday: '',
}

const showIDV = (props, identityVerificationData, vehicleType) => {
  const { idVerificationStatus } = identityVerificationData

  if(isFeatureFlagEnabled(Exp.IDENTITY_VERIFICATION) && props.idvStatus.value === IDVStatus.COMPLETED) {
    return false
  }

  if (
    isFeatureFlagEnabled(Exp.IDENTITY_VERIFICATION) &&
    (
      idVerificationStatus === IDVStatus.NOT_COMPLETED ||
      idVerificationStatus === IDVStatus.CREATED
    )
  ) {
    return true
  }

  // Dx selected non-motor vehicle and passed IDV.
  // Comes back to the app before BGC is submitted and selects motor vehicle then show IDV again
  if (
    isFeatureFlagEnabled(Exp.IDENTITY_VERIFICATION) &&
    props.application.identityTemplateId === TemplateIdTypes['non-motorized'] &&
    TemplateIds[props.vehicleTypes[vehicleType].name] ===
    TemplateIdTypes.motorized
  ) {
    return true
  }

  return false
}

const showIDVFail = (identityVerificationData: any): boolean => {
  const { idVerificationStatus } = identityVerificationData
  return (
    isFeatureFlagEnabled(Exp.IDENTITY_VERIFICATION) &&
    idVerificationStatus === IDVStatus.FAILED
  )
}

const getCurrentStep = (
  props,
  vehicleData,
  identificationData,
  identityVerificationData
) => {
  const { vehicleTypes } = props

  const { vehicleType } = vehicleData
  const { birthday, ssn, licenseNumber, licenseState } = identificationData

  if (vehicleType < 0) {
    return BgcStep.VEHICLE_STEP
  }

  if (showIDV(props, identityVerificationData, vehicleType)) {
    return BgcStep.IDENTITY_VERIFICATION
  }

  if (showIDVFail(identityVerificationData)) {
    return BgcStep.IDENTITY_VERIFICATION_FAIL
  }

  const needsLicenseInfo = vehicleTypes[vehicleType].needs_license_info
  const hasLicenseInfo = licenseNumber && licenseState
  if (
    birthday === '' ||
    (ssn === '' && !isFeatureFlagEnabled(Exp.TOKENIZATION)) ||
    (needsLicenseInfo && !hasLicenseInfo)
  ) {
    return BgcStep.IDENTIFICATION_STEP
  }

  return BgcStep.DISCLOSURE_STEP
}

type VerificationStatus = 'failed' | 'completed' | 'unspecified' | 'started'
const getIDVStatus = (identityVerificationStatus: VerificationStatus) => {
  if (identityVerificationStatus === 'failed') {
    return IDVStatus.FAILED
  }
  if (identityVerificationStatus === 'completed') {
    return IDVStatus.COMPLETED
  }
  return IDVStatus.NOT_COMPLETED
}

export const BackgroundCheckSteps: React.FC<Props> = props => {
  const [vehicleData, setVehicleData] = React.useState(defaultVehicleState)
  const [identificationData, setIdentificationData] = React.useState(
    defaultIdentificationState
  )
  const [
    identityVerificationData,
    setIdentityVerificationData,
  ] = React.useState({
    idVerificationStatus: getIDVStatus(
      props.application.identityVerificationStatus
    ),
    renderToggle: false,
  })
  const [isSsnValid, setIsSsnValid] = React.useState(false)
  const isVgsLoaded = useVgs()
  const form = useVgsForm(isVgsLoaded, setIsSsnValid)
  const step = getCurrentStep(
    props,
    vehicleData,
    identificationData,
    identityVerificationData
  )

  React.useEffect(() => {
    if (isFeatureFlagEnabled(Exp.TOKENIZATION) && isVgsLoaded && form) {
      form
        .field('#ssn', {
          type: 'text',
          name: 'ssn',
          placeholder: '000-00-0000',
          validations: ['/^[0-9]{3}-[0-9]{2}-[0-9]{4}/'],
          css: ssnFieldStyles,
          serializers: [{ name: 'replace', options: { old: '-', new: '' } }],
        })
        .mask('999-99-9999')
    }
  }, [isVgsLoaded, form])

  const { vehicleTypes } = props
  const { vehicleType } = vehicleData
  const needsLicenseInfo =
    vehicleType > 0 ? vehicleTypes[vehicleType].needs_license_info : false

  const handleSubmit = async data => {
    const { doSubmit, uniqueLink } = props
    try {
      if (isFeatureFlagEnabled(Exp.TOKENIZATION)) {
        // Submit via vgs form (tokenized)
        await doSubmit(
          uniqueLink,
          {
            ...vehicleData,
            ...identificationData,
            ...data,
          },
          form
        )
      } else {
        // Submit via DSJ api directly
        return await doSubmit(uniqueLink, {
          ...vehicleData,
          ...identificationData,
          ...data,
        })
      }
    } catch (e) {
      // Handle potential API Errors Here
      // Resets state back to default
      setVehicleData(defaultVehicleState)
      setIdentificationData(defaultIdentificationState)
      throw e
    }
  }

  return (
    <React.Fragment>
      {step === BgcStep.VEHICLE_STEP && (
        <VehicleStep {...props} onSubmit={setVehicleData} />
      )}
      <Hider isHidden={step !== 2}>
        <IdentificationStep
          {...props}
          vgsForm={form}
          isVgsLoaded={isVgsLoaded}
          isSsnValid={isSsnValid}
          onSubmit={setIdentificationData}
          needsLicenseInfo={needsLicenseInfo}
        />
      </Hider>
      {step === BgcStep.IDENTITY_VERIFICATION && (
        <IdentityVerification
          vehicleType={vehicleTypes[vehicleType].name}
          onUpdate={setIdentityVerificationData}
          trackIdentityVerificationSteps={events.trackIdentityVerificationSteps}
          renderToggle={identityVerificationData.renderToggle}
          {...props}
        />
      )}
      {step === BgcStep.IDENTITY_VERIFICATION_FAIL && (
        <IdentityVerificationAwait showContactSupport={false} />
      )}
      {step === BgcStep.DISCLOSURE_STEP && (
        <DisclosureStep
          {...props}
          needsLicenseInfo={needsLicenseInfo}
          identificationData={identificationData}
          setIdentificationData={setIdentificationData}
          onSubmit={handleSubmit}
        />
      )}
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  application: state.application,
  idvStatus: state.idvStatus,
})

const mapDispatchToProps = {
  trackBackgroundCheckSteps: events.trackBackgroundCheckSteps,
  trackIdentityVerificationSteps: events.trackIdentityVerificationSteps,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(BackgroundCheckSteps)
